import React, { useRef, useState } from 'react';
import moment from 'moment';
import fr from 'facet-rest';
import modalConsts from 'Consts/ModalNames';
import { useMeetingContext } from 'Context/MeetingContext';
import useUserContext from 'Context/UserContext';
import { openModal } from 'Helpers/ModalHelper';
import { addQuery } from 'Helpers/Query';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { render } from 'Helpers/template';
import useDebounce from 'Hooks/useDebounce';
import {
  dispatchAccomplishmentsTabRefresh,
  dispatchMeetingContextRefresh,
  dispatchMissionControlLiveRefresh,
  useRefreshActionItem,
} from 'Hooks/useRefreshEvent';
import PropTypes from 'prop-types';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { buildModalQuery } from 'Consts/ModalQueries';
import { fullDateAtTime } from 'Consts/DateFormats';

const EmailTabContext = React.createContext(undefined);

export const dialogNameMap = {
  accomplishments: 'accomplishments',
  investments: 'investments',
  emailType: 'emailType',
  scheduleSend: 'scheduleSend',
  saveTemplate: 'saveTemplate',
  deleteTemplate: 'deleteTemplate',
};

const getSelectedActionItemMap = (actionItems, selectedActionItemIds) => {
  const selectedActionItemMap = selectedActionItemIds.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  return actionItems.reduce((acc, curr) => {
    if (selectedActionItemMap[curr.id()]) {
      acc[curr.id()] = true;
    }

    return acc;
  }, {});
};

const EmailTabProvider = ({
  children,
  householdResource,
  meetingResource,
  meetingTemplateCollection,
  onSaveMeetingTemplate,
  openTabs,
  openTab,
  setActiveTab,
}) => {
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();
  const { userResource, agentResource } = useUserContext();
  const hasMountedRef = useRef(false);
  const { setMeetingResource } = useMeetingContext();
  const { rootNodeProps, logEvent } = useEventTrackingLogEvent({
    nodeName: 'Email Tab',
  });

  const agendaResource = React.useMemo(() => meetingResource?.attr('agendas')?.resources()?.last(), [meetingResource]);

  // Email view
  const [subject, setSubject] = useState(agendaResource?.attr('subject') || '');
  const [content, setContent] = useState(agendaResource?.attr('content') || '');
  const [saved, setSaved] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendType, setSendType] = React.useState('live');

  // Templates
  const [selectedTemplateId, setSelectedTemplateId] = useState();

  // Attachments
  const [attachmentCollection, setAttachmentCollection] = React.useState(null);
  const [loadingAttachments, setLoadingAttachments] = React.useState(false);
  const [attachmentProgress, setAttachmentProgress] = React.useState(0);

  // Action items
  const [clientActionItems, setClientActionItems] = useState([]);
  const [meetingActionItemIds, setMeetingActionItemIds] = useState([]);
  const [plannerActionItems, setPlannerActionItems] = useState([]);
  const [loadingPlannerActionItems, setLoadingPlannerActionItems] = useState(false);
  const [loadingClientActionItems, setLoadingClientActionItems] = useState(false);

  // Dialogs
  const [dialogName, setDialogName] = useState(null);

  // Functions -- Templates
  const getSelectedTemplateById = () => meetingTemplateCollection
    .resources()
    .list()
    .find(resource => resource.id() === selectedTemplateId);

  const getSelectedTemplate = () => {
    const template = getSelectedTemplateById();
    if (!template) {
      return meetingTemplateCollection.create();
    }
    return template;
  };

  const handleInsertTemplate = (template) => {
    if (!template) return;
    const date = moment(meetingResource.attr('startTime')).format(
      'MMMM Do, YYYY',
    );
    const time = moment(meetingResource.attr('startTime')).format(
      'h:mma',
    );
    const templateContent = render(template.attr('content'), {
      eventDate: `${date} at ${time}`,
      agentName: userResource.attr('firstName'),
    });
    const templateSubject = template.attr('subject');
    setContent(templateContent);
    setSubject(templateSubject);
    agendaResource.attr('subject', templateSubject);
    agendaResource.attr('content', templateContent);
    agendaResource.attr('templateID', template.id());
  };

  const handleInsertTemplateToken = (token) => {
    const contentWithToken = content + token;
    setContent(contentWithToken);
    agendaResource.attr('content', contentWithToken);
  };

  const handleSaveTemplate = async (template) => {
    let meetingTemplate = template;
    // If it's a Facet template or the user hasn't selected a template, then we need to make a new version of the template
    if (meetingTemplate.id() && !meetingTemplate.attr('agentId')) {
      meetingTemplate = meetingTemplateCollection
        .create()
        .attr('name', meetingTemplate.attr('name'));
    }
    try {
      await meetingTemplate
        .attr('subject', subject)
        .attr('content', content)
        .attr('agentId', agentResource.id())
        .submit();
      onSaveMeetingTemplate();
      displaySuccess('Successfully saved template');
    } catch (err) {
      if (err.status === 409) {
        displayErrorMessage(err, 'A template with this name already exists');
      } else {
        displayErrorMessage(err, 'Failed to save meeting template');
      }
    }
  };

  const handleDeleteMeetingTemplate = async (selectedTemplate) => {
    if (selectedTemplate) {
      try {
        await selectedTemplate.delete();
        handleSaveTemplate();
        displaySuccess('Template was successfully deleted.');
      } catch (e) {
        displayErrorMessage(e, e.errors);
      }
    }
  };

  // Functions -- Attachments
  const refreshAttachments = () => {
    if (meetingResource) {
      meetingResource
        .relationship('attachments')
        .link('related')
        .Get()
        .then((coll) => {
          setAttachmentCollection(coll);
        });
    }
  };

  const onStartFileUpload = () => setLoadingAttachments(true);

  const onFinishFileUpload = () => setLoadingAttachments(false);

  // Functions -- Action Items
  const handleClickActionItem = (ai) => {
    const actionItemRaw = ai.export();
    logEvent('Mission Control: Clicked Action Item', {
      actionItemId: actionItemRaw.id,
    });
    actionItemRaw.id = parseInt(actionItemRaw.id, 10);
    addQuery({ todoId: actionItemRaw.id });
    openModal(modalConsts.simpleActionItemModal);
  };

  const handleToggleActionItem = (ai, clientAssigned) => {
    if (meetingActionItemIds.includes(ai.id())) {
      ai.delete().then((coll) => {
        setMeetingActionItemIds(meetingActionItemIds.filter(id => id !== coll.resources().head().id()));
      })
        .catch((err) => {
          displayErrorMessage(err, err.errors);
        });
    } else {
      fr.http.Put(meetingResource.relationship('simpleActionItemLinks').link('related').url({ params: { id: ai.id() } }), {}).then((res) => {
        setMeetingActionItemIds([...meetingActionItemIds, res.data[0].id]);
        if (clientAssigned) {
          const updatedActionItems = clientActionItems.map(r => (r.id() === res.data[0].id ? fr.resource(res.data[0]) : r));
          setClientActionItems(updatedActionItems);
        } else {
          const updatedActionItems = plannerActionItems.map(r => (r.id() === res.data[0].id ? fr.resource(res.data[0]) : r));
          setPlannerActionItems(updatedActionItems);
        }
      });
    }
  };

  const handleClickAddActionItem = (isClientAssigned) => {
    openModal(
      buildModalQuery(modalConsts.simpleActionItemGroupModal, {
        defaultValues: {
          priority: 'medium',
          dueDate: moment
            .utc()
            .add(1, 'month')
            .toDate(),
          assignedTo: isClientAssigned ? 'Household' : 'Me',
        },
      }),
    );
  };

  const refreshActionItems = async () => {
    setLoadingPlannerActionItems(true);
    setLoadingClientActionItems(true);

    try {
      // Get all available links
      const links = [
        householdResource?.relationship('simpleActionItemsAssignedToHouseholdLinks')?.link('related'),
        householdResource?.relationship('simpleActionItemsAssignedToFacetLinks')?.link('related'),
        meetingResource?.relationship('simpleActionItemLinks')?.link('related'),
      ].filter(v => !!v);

      const [
        clientActionItemsResponse,
        plannerActionItemsResponse,
        meetingActionItemsResponse,
      ] = await Promise.all(links.map(link => link.get()));

      setClientActionItems(clientActionItemsResponse.resources().list());
      setPlannerActionItems(plannerActionItemsResponse.resources().list());
      setMeetingActionItemIds(meetingActionItemsResponse.resources().list().map(v => v.id()));
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }

    setLoadingPlannerActionItems(false);
    setLoadingClientActionItems(false);
  };

  // Functions -- Agenda
  const handleSaveMeeting = React.useCallback(async (meetingAgendaResource, isLiveEmail) => {
    try {
      await meetingAgendaResource.submit();
      if (!hasMountedRef.current) {
        // short circuit if no longer mounted
        return;
      }
      const [refreshedMeetingResource] = await meetingResource.link('self').get();

      setMeetingResource(refreshedMeetingResource.copy());

      // If the email is a live one then send update subject/content to reset it
      if (isLiveEmail) {
        // eslint-disable-next-line newline-per-chained-call
        const updatedMostRecentAgenda = refreshedMeetingResource.attr('agendas').resources().last();

        setSubject(updatedMostRecentAgenda?.attr('subject'));

        // This will trigger an autoSave because CKEditor will call its "onChange" function as
        // a result of it (asynchronously) observing the change by calling setContent here.
        // This will cause onChangeBody to run which autosaves. We don't want this to happen in this case
        // because the value is being programmatically updated rather than actually typed. The updated value
        // does not need to dirty.
        setContent(updatedMostRecentAgenda?.attr('content'));

        const attachmentsResp = await refreshedMeetingResource.relationship('attachments')
          .link('related')
          .get();

        setAttachmentCollection(attachmentsResp);
      }

      const isFollowUp = meetingAgendaResource.attr('messageType') === 'follow-up';
      if (isFollowUp) {
        setDialogName(dialogNameMap.accomplishments);
      }

      setSaved(true);
    } catch (err) {
      const message = Array.isArray(err?.errors)
        ? err.errors[0]?.detail
        : 'No detail error message';

      displayErrorMessage(err, message);
    }

    dispatchMeetingContextRefresh();
  }, []);

  const applyAgendaAutoSave = useDebounce((meetingAgenda) => {
    if (agendaResource) {
      return handleSaveMeeting(
        meetingAgenda,
        false,
      );
    }

    return Promise.resolve();
  }, 1500, handleSaveMeeting);

  const handleUpdateSubject = React.useCallback((updatedSubject) => {
    setSaved(false);
    setSubject(updatedSubject);
    agendaResource.attr('subject', updatedSubject);
    applyAgendaAutoSave(agendaResource);
  }, [applyAgendaAutoSave, agendaResource]);

  const handleUpdateContent = React.useCallback((updatedContent) => {
    // onChangeBody is tricky because it does not get called when a user typed, but rather
    // asynchronously once CKEditor has processed the change.
    // IMPORTANT: in the case of saving a meeting the content will match what CKEditor thinks it is
    // and in this case we do NOT want to save again.
    if (updatedContent === content) {
      return;
    }

    // Otherwise, content and v don't match so we should save the value
    setSaved(false);
    setContent(updatedContent);
    agendaResource.attr('content', updatedContent);
    applyAgendaAutoSave(agendaResource);
  }, [applyAgendaAutoSave, agendaResource]);

  const handleRestoreGeneratedContent = React.useCallback(() => {
    handleUpdateContent(agendaResource.attr('generatedContent'));
  }, [handleUpdateContent, agendaResource]);

  const handleSaveDraft = React.useCallback(() => {
    return handleSaveMeeting(
      agendaResource.copy().attributes({ content, subject }),
      false,
    );
  }, [agendaResource, subject, content]);

  const handleClickScheduleSend = React.useCallback(() => {
    setSendType('scheduled');
    setDialogName(dialogNameMap.scheduleSend);
  }, [agendaResource]);

  // Submit the schedule send dialog should just transfer you to the actual send dialog
  const handleSubmitScheduleSend = async () => {
    setDialogName(dialogNameMap.emailType);
  };

  const handleClickSendToClient = () => {
    setSendType('live');
    setDialogName(dialogNameMap.emailType);
  };

  const handleConfirmScheduleSend = React.useCallback(async () => {
    logEvent('Mission Control: Confirmed Schedule Send');
    logEvent('Scheduled Send: Scheduling meeting agenda', {
      householdId: agendaResource.attr('householdId'),
      meetingDate: moment(agendaResource.attr('startTime')).format(fullDateAtTime),
      scheduledDate: moment(agendaResource.attr('scheduledSendCustomDate')).format(fullDateAtTime),
      daysUntilMeeting: moment(agendaResource.attr('startTime')).diff(moment(), 'days'),
      messageType: agendaResource.attr('messageType'),
    });

    setSendingEmail(true);
    await handleSaveMeeting(agendaResource.copy().attr('sendEmail', 'scheduled-email'), true);
    dispatchMissionControlLiveRefresh();
    setSendingEmail(false);
  }, [agendaResource]);

  // When you confirm sending to a client
  const handleConfirmImmediateSend = React.useCallback(async () => {
    logEvent('Mission Control: Confirmed Send Email');

    await handleSaveMeeting(
      agendaResource.copy()
        .attr('scheduledSendType', null)
        .attr('scheduledSendCustomDate', null)
        .attr('sendAt', null)
        .attr('sendEmail', 'live-email'),
      true,
    );

    dispatchMissionControlLiveRefresh();
    setSendingEmail(false);
  }, [agendaResource]);

  // Functions -- Dialogs
  const onAccomplishmentModalClose = () => {
    // Check if accomplishments tab is already open
    const existingTab = openTabs.find(t => t.name === 'accomplishments');

    // If the accomplishments tab is not open then open it, otherwise refresh the data and set it as an active tab
    if (!existingTab) {
      openTab('accomplishments', 0);
      setActiveTab(0, 'accomplishments');
    } else {
      dispatchAccomplishmentsTabRefresh();
      setActiveTab(existingTab.column, existingTab.name);
    }

    // Open the investment modal if that makes sense to do
    if (meetingResource.relationship(fr.relationships.justWinPM1.name)
        || meetingResource.relationship(fr.relationships.justWinPM2.name)) {
      setDialogName(dialogNameMap.investments);
    } else {
      setDialogName(null);
    }
  };

  const onModalClose = () => {
    setDialogName(null);
  };

  React.useEffect(() => {
    hasMountedRef.current = true;
    refreshActionItems();
    refreshAttachments();

    return () => {
      hasMountedRef.current = false;
    };
  }, []);

  useRefreshActionItem(() => {
    refreshActionItems();
  }, [meetingResource, householdResource]);

  const memoizedValue = React.useMemo(() => {
    return {
      // Tab
      rootNodeProps,
      householdResource,
      meetingResource,
      meetingTemplateCollection,

      // Agenda
      agendaResource,
      subject,
      setSubject,
      content,
      setContent,
      saved,
      sendingEmail,
      applyAgendaAutoSave,
      handleUpdateSubject,
      handleUpdateContent,
      handleRestoreGeneratedContent,
      handleSaveDraft,
      handleClickSendToClient, // Click to open the email type dialog (agenda for an upcoming meeting? follow up?)
      handleClickScheduleSend, // Click on the button to open the schedule send dialog
      handleSubmitScheduleSend, // Submit the schedule send dialog and progress to the email type dialog
      handleConfirmScheduleSend, // From the email type dialog, confirm sending in the case where it's a scheduled email
      handleConfirmImmediateSend, // From the email type dialog, confirm sending in the case where it's an immediately sent email
      isSendingDisabled: agendaResource.attr('generatedContentUpdatedAt') && agendaResource.isTruthy('isSameAsGeneratedContent'),
      sendType,

      // Templates
      handleInsertTemplate,
      handleInsertTemplateToken,
      getSelectedTemplate,
      setSelectedTemplateId,
      handleSaveTemplate,
      handleDeleteMeetingTemplate,

      // Attachments
      attachmentCollection,
      onStartFileUpload,
      onFinishFileUpload,
      setAttachmentProgress,
      attachmentProgress,
      refreshAttachments,
      loadingAttachments,

      // Action Items
      clientActionItems,
      selectedClientActionItemMap: getSelectedActionItemMap(clientActionItems, meetingActionItemIds),
      plannerActionItems,
      selectedPlannerActionItemMap: getSelectedActionItemMap(plannerActionItems, meetingActionItemIds),
      handleToggleActionItem,
      handleClickActionItem,
      handleClickAddActionItem,
      loadingPlannerActionItems,
      loadingClientActionItems,

      // Dialogs
      dialogName, // The name of the dialog that should be opened if any
      onModalClose,
      onAccomplishmentModalClose,
    };
  }, [
    // Tab
    rootNodeProps,
    householdResource,
    meetingResource,
    meetingTemplateCollection,

    // Agenda
    agendaResource,
    subject,
    setSubject,
    content,
    setContent,
    saved,
    sendingEmail,
    applyAgendaAutoSave,
    handleUpdateSubject,
    handleUpdateContent,
    handleRestoreGeneratedContent,
    handleSaveDraft,
    handleClickSendToClient,
    handleClickScheduleSend,
    handleConfirmScheduleSend,
    handleConfirmImmediateSend,
    sendType,

    // Templates
    handleInsertTemplate,
    getSelectedTemplate,
    setSelectedTemplateId,
    handleSaveTemplate,
    handleDeleteMeetingTemplate,

    // Attachments
    attachmentCollection,
    loadingAttachments,
    refreshAttachments,
    onStartFileUpload,
    onFinishFileUpload,
    setAttachmentProgress,
    attachmentProgress,

    // Action Items
    clientActionItems,
    meetingActionItemIds,
    plannerActionItems,
    handleToggleActionItem,
    handleClickActionItem,
    handleClickAddActionItem,
    loadingPlannerActionItems,
    loadingClientActionItems,

    // Dialogs
    dialogName, // The name of the dialog that should be opened if any
    onModalClose,
    onAccomplishmentModalClose,
  ]);

  return (
    <EmailTabContext.Provider value={memoizedValue}>
      {children}
    </EmailTabContext.Provider>
  );
};

// Use EmailTab State Context
const useEmailTabContext = () => {
  const context = React.useContext(EmailTabContext);
  if (context === undefined) {
    throw new Error('useEmailTabContext must be used within a EmailTabProvider');
  }
  return context;
};

EmailTabProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  householdResource: PropTypes.object.isRequired,
  meetingResource: PropTypes.object.isRequired,
  meetingTemplateCollection: PropTypes.object.isRequired,
  onSaveMeetingTemplate: PropTypes.func.isRequired,
  openTabs: PropTypes.array.isRequired,
  openTab: PropTypes.func.isRequired,
  setActiveTab: PropTypes.func.isRequired,
};

export {
  EmailTabProvider,
  useEmailTabContext,
};
